import React, { useState, useEffect, useRef } from 'react';
import qs from 'qs';

import { Alert } from '../../components/Alert'
import { Button, HollowButton, NakedButton } from '../../components/Button';
import { CardContainer, ColContainer, Container } from '../../components/Container';
import { CheckboxInput } from '../../components/CheckboxInput';
import { DateInput } from '../../components/DateInput';
import { Byline, H1, H2, HR } from '../../components/Typography';
import { Input } from '../../components/Input';
import { PhoneNumberInput } from '../../components/PhoneNumberInput';
import { Loading, LoadingError } from '../../components/LoadingFork';
import { SelectInput } from '../../components/SelectInput';
import { StripeElementsInput } from '../../components/StripeElementsInput';
import { GraphqlErrorMessage, ValidationErrorMessage } from '../../components/ErrorMessage';
import { useFormState } from 'react-use-form-state'
import { useMutation, useQuery } from "@apollo/client";
import { withAppConfig } from '../../components/AppConfigContext';
import { withInstallation } from '../../components/InstallationContext';
import ReactModal from 'react-modal'
import { format as formatDate, isSameDay, lightFormat, isAfter } from 'date-fns'
import { details, determineDays, requirements, } from '../../lib/rateCardUtils';
import { ParkingSummary, ParkingSummaryHeader } from './components/ParkingSummary';
import drivoApollo from '../../lib/drivoApollo';
import gql from 'graphql-tag'

import { allActiveRateCards, bookingById, eventById, getStripePaymentMethods, parklioAvailableBays } from './queries'
import { bookingContactsQuery } from '../Concierge/vip-routing/queries'
import { createBookingMutation, updateBookingMutation } from './mutations'
import { AllocatedBaySection } from '../../components/bayAccess/AllocatedBaySection'
import { isParklioBayAccessEnabled } from '../../lib/parklioFeature'
import { css } from "glamor";
import * as enums from '../../lib/enums'
import { formatPlate } from "../../lib/misc";
import { toFriendlyBookingError } from "../../lib/bookingErrors";
import { DateTime } from "luxon";
import { TextArea } from '../../components/TextArea';
import { isPossiblePhoneNumber, parsePhoneNumber } from 'react-phone-number-input';
import { orange, red } from '../../theme';
import {isConciergePageEnabled} from "../../lib/conciergeFeature";

export function toCountryCode(country) {
    return typeof country === 'string' && country.length === 2
        ? country.toUpperCase()
        : undefined
}

export function toE164(raw, country) {
    if (!raw) return ''

    return parsePhoneNumber(raw, country)?.number ?? raw
}

export function ArchivedRateCard(rateCard) {
    return <Alert flavour='danger' spaceAbove='medium'>
        <strong>Guest card no-longer available</strong>
        <p>
            The guest card used <strong>{rateCard.name}</strong> is archived.
            You will need to select a new guest card to continue.
        </p>
    </Alert>
}

export function RateCardOptions({ rateCards, onChange, value }) {

    function _onChange(e) {
        const rateCard = rateCards.find(x => x.id === e.target.value)
        onChange(rateCard)
    }

    // remove duplicates by id
    const cardsMap = {}
    const lineageCounts = {}
    rateCards.forEach((card) => {
        cardsMap[card.id] = card
    })

    // count if something is repeated
    for (const id in cardsMap) {
        const { lineageId } = cardsMap[id]
        lineageCounts[lineageId] = (lineageCounts[lineageId] || 0) + 1
    }

    const options = []
    for (const id in cardsMap) {
        const {
            name,
            lineageId,
            isArchived,
            version
        } = cardsMap[id]

        // differentiate rateCards of same lineage by version
        let label = `${name}`
        if (lineageCounts[lineageId] > 1) {
            label = `${label} (version ${version})`
        } else if (isArchived) {
            label = `${label} (archived)`
        }

        options.push({
            label,
            value: id,
            disabled: isArchived
        })
    }

    // sort by label
    options.sort((a, b) => a.label.localeCompare(b.label))

    return <SelectInput
        label='Guest Card'
        placeholder='Select a guest card'
        autoComplete='off'
        options={options}
        type='select'
        onChange={_onChange}
        value={value ? value.id : undefined}
    />
}

export function BookingHeader({ title, startedAt, children, installationId }) {
    const { data: capacity, loading: capacityLoading, refetch: capacityRefetch } = useQuery(gql`
        query Capacity($installationId:ID!)
        {
            getCapacity(installationId:$installationId){
                id
                capacity
                limitToSanctionedPlatesEnabled
                limitToSanctionedPlateCapacity
                bookingsUntilFull
            }
        }
    `, {
        variables: { installationId: installationId },
        fetchPolicy: "no-cache"
    });

    const vacantSpots = isNaN(capacity?.getCapacity?.bookingsUntilFull) || capacity?.getCapacity?.bookingsUntilFull <= 0 ? 0 : capacity?.getCapacity?.bookingsUntilFull;

    const checkIfAlmostFull = (parkCapacity) => {
        if (!parkCapacity || parkCapacity <= 0 || isNaN(vacantSpots)) return false;

        const parkedSlots = parkCapacity - vacantSpots;
        const lotFilledRate = parkedSlots / parkCapacity;

        if (vacantSpots === 0 || lotFilledRate >= 0.95) return true;

        return false;
    }

    const almostFull = checkIfAlmostFull(capacity?.getCapacity?.limitToSanctionedPlateCapacity, vacantSpots);

    const dateStr = startedAt ? formatDate(new Date(startedAt), 'h:mm a, EEEE d MMM yyyy') : null;

    return (
        <Container size='large' spaceBelow='medium'>
            <H1 spaceBelow='none'>{title}</H1>
            {almostFull &&
                <H2
                    style={{
                        color: vacantSpots === 0 ? red : orange
                    }}
                >
                    {vacantSpots === 0 ? "Car park is currently full!" : `Remaining vacant spots: ${vacantSpots}`}
                </H2>
            }
            <Byline>Starts at: {!startedAt ? "-" : dateStr}</Byline>
            <HR />
            {children}
        </Container>
    )
}

export function isToday(date, timezone) {
    if (!date) return false;

    return date.hasSame?.(DateTime.now().setZone(timezone), "day")
}

const modalStyles = css({
    display: 'block',
    background: 'white',
    margin: 10,
    padding: 20,
    borderRadius: 6,
    outline: 'none',
    minWidth: 300,
    maxWidth: 500,
}).toString();

const modalOverlayStyles = css({
    backgroundColor: 'rgba(0,0,0,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'fixed',
    zIndex: 100000,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
}).toString();

export const BookingForm = withAppConfig(function BookingForm({
    defaultPlate,
    eventId,
    installation,
    facility,
    startedAt,
    booking,
    rateCards,
    appConfig,
    onSubmit,
    onCancel
}) {
    if (booking) {
        rateCards = [...rateCards, booking.rateCard]
    }

    const countryCode = toCountryCode(facility?.city?.country)

    const [{
        errors,
        values,
        setField,
    }, { text, email, raw, select }] = useFormState({
        rateCard: booking ? booking.rateCard : null,
        guestPlate: defaultPlate,

        // Daily fields
        startDay: booking ? DateTime.fromISO(booking.startDay, { locale: "en-AU" }).setZone(installation.timeZoneName).toFormat('yyyy-MM-dd') : lightFormat(new Date(), 'yyyy-MM-dd'),
        endDay: booking ? DateTime.fromISO(booking.endDay, { locale: "en-AU" }).setZone(installation.timeZoneName).toFormat('yyyy-MM-dd') : lightFormat(new Date(), 'yyyy-MM-dd'),
        staffName: booking ? booking.staffName : '',
        reason: booking?.reason || '',
        // Guest fields
        guestName: booking ? booking.guestName : '',
        guestEmail: booking ? booking.guestEmail : '',
        guestCompany: booking ? booking.guestCompany : '',
        guestDepartment: booking ? booking.guestDepartment : '',
        guestPhone: toE164(booking?.guestPhone, countryCode),
        guestRoom: booking ? booking.guestRoom : '',
        guestApartmentNumber: booking ? booking.guestApartmentNumber : '',
        guestReservation: booking ? booking.guestReservation : '',
        guestEmployeeCode: booking ? booking.guestEmployeeCode : '',
        guestPosition: booking ? booking.guestPosition : '',
        stripeSource: booking?.stripeSource || null,
        stripeCustomerId: booking?.stripeCustomerId || null,
        status: booking ? booking.status : undefined,
        carType: booking ? booking.carType : '',
        carNotes: booking ? booking.carNotes : '',
        isVip: booking ? Boolean(booking.isVip) : false,
    })
    const [vipContactIds, setVipContactIds] = useState(() =>
        (booking?.vipContacts ?? []).map((v) => v.contactId),
    );

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);
    const [isStaffModalOpen, setStaffModalOpen] = useState(false);

    const [createBooking, { error: createError }] = useMutation(createBookingMutation);

    const [updateBooking, { error: updateError }] = useMutation(updateBookingMutation);

    const { startDay, endDay, rateCard } = values;

    // Parklio bay picker (DRI-2874): shown only when the hotel is Parklio-capable
    // AND the selected card opts into bay assignment (rateCard.parklioBayAssignment).
    // Availability is resolved by the API with the SAME date logic create uses, so
    // the picked bay matches what the booking will claim.
    const parklioEnabled =
      isParklioBayAccessEnabled(facility) && rateCard?.parklioBayAssignment === true;
    const [selectedBayId, setSelectedBayId] = useState(null);
    const {
        data: baysData,
        loading: baysLoading,
        error: baysError,
        refetch: refetchBays,
    } = useQuery(parklioAvailableBays, {
        variables: { rateCardId: rateCard?.id, startDay, endDay },
        skip: !parklioEnabled || !rateCard || !startDay || !endDay || !!booking,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });
    const availableBays = parklioEnabled
        ? baysData?.parklioAvailableBays ?? []
        : [];
    const selectedBay =
        availableBays.find((bay) => bay.id === selectedBayId) ?? null;
    // Block submit until availability has resolved to a bay the staff can actually
    // get: while loading, on a load error (availableBays falls back to []), or when
    // no valid bay is selected (none free / stale pick). Never let a Parklio booking
    // through without a confirmed bay.
    const parklioBlocksSubmit =
        parklioEnabled && !booking && (baysLoading || !!baysError || !selectedBay);
    const vipRoutingEnabled = isConciergePageEnabled(facility, "vipRouting");
    const showVipContacts = vipRoutingEnabled && rateCard?.isValet === true && Boolean(values.isVip);

    const { data: contactsData } = useQuery(bookingContactsQuery, {
        variables: { installationId: installation.id },
        skip: !showVipContacts,
    });
    const vipContacts = contactsData?.contacts ?? [];

    /* Contacts already paged cannot be un-paged, so they stay ticked. */
    const pagedVipContactIds = (booking?.vipContacts ?? [])
        .filter((v) => v.alertedAt)
        .map((v) => v.contactId);

    const toggleVipContact = (id) =>
        setVipContactIds((ids) =>
            ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id],
        );

    const cardVipContactId = rateCard?.defaultVipContactId ?? null;
    const defaultedCardId = useRef(booking?.isVip ? booking?.rateCard?.id ?? null : null);

    useEffect(() => {
        if (!values.isVip || !rateCard?.id) return;
        if (defaultedCardId.current === rateCard.id) return;

        defaultedCardId.current = rateCard.id;
        /* Paged contacts have already been alerted, so they survive the reset. */
        setVipContactIds(
            [...pagedVipContactIds, cardVipContactId].filter(
                (id, i, ids) => id && ids.indexOf(id) === i,
            ),
        );
    }, [cardVipContactId, values.isVip, rateCard?.id]);

    const isMonthly = rateCard?.type === "MONTHLY";
    const isFixedPackage =
        rateCard?.type === "DAILY" &&
        rateCard?.calculationMode === "MULTI_DATE_FIXED_PACKAGE";

    /*
     * FIXED_SESSION cards are only bookable on their active days. Days not in
     * activeDaysOfWeek (e.g. Fri/weekend) are blocked in the date picker.
     */
    const isFixedSession = rateCard?.type === "FIXED_SESSION";
    const fixedSessionActiveDays = (() => {
        const raw = rateCard?.activeDaysOfWeek;
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        try { return JSON.parse(raw); } catch { return []; }
    })();
    const isInactiveDayForCard = (day) => {
        if (!isFixedSession || fixedSessionActiveDays.length === 0) return false;
        // `day` is a moment object; isoWeekday() is 1 (Mon) … 7 (Sun).
        return !fixedSessionActiveDays.includes(day.isoWeekday());
    };
    /*
     * The date defaults to today, which may fall on an inactive day for a
     * FIXED_SESSION card. Block submission (and flag the field) when the
     * selected start day is not one of the card's active days.
     */
    const isFixedSessionInactiveDate =
        isFixedSession &&
        fixedSessionActiveDays.length > 0 &&
        !!startDay &&
        !fixedSessionActiveDays.includes(
            DateTime.fromISO(startDay, { zone: installation.timeZoneName }).weekday,
        );
    const fixedSessionActiveDayNames = [...fixedSessionActiveDays]
        .sort((a, b) => a - b)
        .map((d) => enums.ISO_DAYS_OF_WEEK.find((x) => x.value === d)?.label)
        .filter(Boolean)
        .join(', ');

    const [paymentMethod, setPaymentMethod] = useState('');
    /* Set only when the operator confirms a card via Stripe confirmSetup in
     * THIS session. Unlike values.stripeCustomerId (prefilled from an existing
     * booking on edit), this proves a payment method was actually confirmed
     * here, so it can safely relax the submit guard during the async gap while
     * getStripePaymentMethods is still fetching. */
    const [cardConfirmedThisSession, setCardConfirmedThisSession] = useState(false);
    const required = rateCard ? requirements(rateCard) : {};
    const isRateCardSingleDay = rateCard?.calculationMode === "SINGLE_DAY" || rateCard?.type === "MONTHLY";
    const isRateCardFOC = rateCard?.name?.toLowerCase().match('free of charge|free|foc') != null;

    // Auto-snap dates to month boundaries for MONTHLY rate cards
    useEffect(() => {
        if (!isMonthly || !startDay) return;
        const dt = DateTime.fromISO(startDay, { zone: installation.timeZoneName, locale: "en-AU" });
        if (!dt.isValid) return;

        const firstOfMonth = dt.startOf('month').toISO();
        const lastOfMonth = dt.endOf('month').startOf('day').toISO();

        if (startDay !== firstOfMonth) {
            setField('startDay', firstOfMonth);
        }
        if (endDay !== lastOfMonth) {
            setField('endDay', lastOfMonth);
        }
    }, [isMonthly, startDay, installation.timeZoneName]);

    // Auto-fill & lock dates from the rate card for MULTI_DATE_FIXED_PACKAGE.
    // The API enforces this on save too — locking the UI just prevents drift.
    useEffect(() => {
        if (!isFixedPackage) return;
        const fixedStart = rateCard?.fixedBookingStartDate;
        const fixedEnd = rateCard?.fixedBookingEndDate;
        if (!fixedStart || !fixedEnd) return;

        if (startDay !== fixedStart) setField('startDay', fixedStart);
        if (endDay !== fixedEnd) setField('endDay', fixedEnd);
    }, [
        isFixedPackage,
        rateCard?.fixedBookingStartDate,
        rateCard?.fixedBookingEndDate,
    ]);

    const badPhone = Boolean(values.guestPhone) && !isPossiblePhoneNumber(values.guestPhone)

    const hasErrors = Object.values(errors).some(x => x)
    const [submitError, setSubmitError] = useState(createError || updateError);
    const errorAnchorRef = useRef(null);
    useEffect(() => {
        if (submitError && errorAnchorRef.current) {
            errorAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [submitError]);
    const canSubmit =
        !isSubmitting &&
        !hasErrors && rateCard &&
        (!required.guestName || values.guestName) &&
        (!required.guestEmail || values.guestEmail) &&
        (!required.guestCompany || values.guestCompany) &&
        (!required.guestDepartment || values.guestDepartment) &&
        (!required.guestPhone || values.guestPhone) &&
        !badPhone &&
        (!required.includeCarType || values.carType) &&
        (!required.includeCarNotes || values.carNotes) &&
        (!required.guestReservation || values.guestReservation) &&
        (!required.guestEmployeeCode || values.guestEmployeeCode) &&
        (!required.guestPosition || values.guestPosition) &&
        (!required.guestRoom || values.guestRoom) &&
        (!required.guestApartmentNumber || values.guestApartmentNumber) &&
        (values.staffName || !isModalOpen) &&
        (!isModalOpen || !isRateCardFOC || values.reason) &&
        (!required.endDay || endDay) &&
        !isFixedSessionInactiveDate &&
        !rateCard.isArchived &&
        !parklioBlocksSubmit

    const fetchPaymentMethod = async (id) => {
        if (!id) return;
        try {
            const paymentMethod = await drivoApollo.query({
                query: getStripePaymentMethods,
                variables: {
                    customerId: id,
                    installationId: installation.id,
                },
            });
            setPaymentMethod(paymentMethod.data.getStripePaymentMethods[0]);
        } catch (error) {
            console.error("Error fetching payment method:", error);
        }
    };

    useEffect(() => {
        fetchPaymentMethod(booking?.stripeCustomerId);
    }, []);

    useEffect(() => {
        fetchPaymentMethod(values.stripeCustomerId);
    }, [values.stripeCustomerId]);

    function validateStartDay(startDay, { rateCard, endDay }) {
        if (!startDay || !endDay || !rateCard) return

        if (
            rateCard?.calculationMode === "MULTI_DATE_FIXED_PACKAGE" &&
            rateCard?.fixedBookingStartDate &&
            startDay !== rateCard.fixedBookingStartDate
        ) {
            return `Start date is fixed by the rate card`
        }

        if (!rateCard?.allowFutureDate && isAfter(new Date(startDay), new Date())) {
            return `Booking date cannot be in the future for this rate card`
        }

        if (rateCard?.calculationMode === "SINGLE_DAY") {
            setField('endDay', startDay);
            return;
        }

        try {
            determineDays(startDay, endDay, rateCard, installation.timeZoneName)
        } catch (e) {
            return `Invalid booking start day (${e.message})`
        }

        setField('endDay', endDay); // set again to redo validation check
    }

    function validateEndDay(endDay, { rateCard, startDay }) {
        if (!startDay || !endDay || !rateCard) return

        if (
            rateCard?.calculationMode === "MULTI_DATE_FIXED_PACKAGE" &&
            rateCard?.fixedBookingEndDate &&
            endDay !== rateCard.fixedBookingEndDate
        ) {
            return `End date is fixed by the rate card`
        }

        try {
            determineDays(startDay, endDay, rateCard, installation.timeZoneName)
        } catch (e) {
            return `Invalid booking end day (${e.message})`
        }
        setField('startDay', startDay); // set again to redo validation check
    }

    const startDateTime = rateCard && startDay && DateTime.fromISO(startDay, { zone: installation.timeZoneName })
        .plus({
            minutes: rateCard.entryAfterTime,
        })
        .toISO() || undefined;
    const endDateTime = rateCard && endDay && DateTime.fromISO(endDay, { zone: installation.timeZoneName })
        .plus({
            minutes: rateCard.exitBeforeTime,
        })
        .toISO() || undefined;

    async function _onSubmit() {
        /* A card confirmed in this session (cardConfirmedThisSession) counts as
         * a confirmed payment method even while the getStripePaymentMethods
         * lookup is still in flight (e.g. methods saved via Stripe Link). We do
         * NOT trust a prefilled values.stripeCustomerId here: on edit it is
         * loaded from the existing booking and may point at a customer whose
         * card was since removed, which must still block submission. */
        if (required.paymentStripe && !paymentMethod && !cardConfirmedThisSession) {
            setSubmitError({ paymentStripe: true, message: 'Stripe Rate Card requires confirmed payment method.' });
            return;
        }

        setIsSubmitting(true);
        console.log({
            startDay,
            fromISO: DateTime.fromISO(startDay, { zone: installation.timeZoneName }),
            startOfDay: DateTime.fromISO(startDay, { zone: installation.timeZoneName }).startOf('day'),
            toISO: DateTime.fromISO(startDay, { zone: installation.timeZoneName }).startOf('day').toISODate(),
        })

        const sharedValues = {
            installationId: installation.id,
            rateCardId: rateCard ? rateCard.id : null,
            plate: formatPlate(values.guestPlate),

            // Daily fields
            startDay: startDateTime,
            endDay: endDateTime,

            // Guest fields
            staffName: values && values.staffName ? values.staffName : null,
            reason: values && values.reason ? values.reason : null,
            guestName: required.guestName ? values.guestName : '',
            guestCompany: required.guestCompany ? values.guestCompany : '',
            guestDepartment: required.guestDepartment ? values.guestDepartment : '',
            guestPhone: required.guestPhone ? values.guestPhone : '',
            guestEmail: required.guestEmail ? values.guestEmail : '',
            guestRoom: required.guestRoom ? values.guestRoom : '',
            guestApartmentNumber: required.guestApartmentNumber ? values.guestApartmentNumber : '',
            guestReservation: required.guestReservation ? values.guestReservation : '',
            guestEmployeeCode: required.guestEmployeeCode ? values.guestEmployeeCode : '',
            guestPosition: required.guestPosition ? values.guestPosition : '',
            stripeCustomerId: values.stripeCustomerId || null,
            status: values.status,
            carType: required.includeCarType ? values.carType : '',
            carNotes: required.includeCarNotes ? values.carNotes : '',
            isVip: Boolean(values.isVip),
            vipContactIds: showVipContacts ? vipContactIds : [],
        }

        try {
            if (booking) {
                const { data } = await updateBooking({
                    variables: {
                        bookingId: booking.id,
                        ...sharedValues
                    }
                })

                onSubmit(data.updateBooking)
            } else {
                const { data } = await createBooking({
                    variables: {
                        installationId: installation.id,
                        entryEventId: eventId,
                        bayId: parklioEnabled ? selectedBayId : undefined,
                        ...sharedValues
                    }
                })

                onSubmit(data.createBooking)
            }
        } catch (err) {
            setSubmitError(toFriendlyBookingError(err));
            // DRI-2874: the pick may have been rejected (bay taken) — refresh the
            // free-bay list so staff can re-pick before retrying (create only).
            if (parklioEnabled && !booking) refetchBays();
        } finally {
            setIsSubmitting(false);
        }
    }

    return <form>
        <Container size='large'>
            <span ref={errorAnchorRef} />
            {submitError && <GraphqlErrorMessage error={submitError} />}
            {rateCard ? <>
                {rateCard.isArchived && <ArchivedRateCard rateCard={rateCard} />}
            </> : null}
            {isFixedSessionInactiveDate && (
                <Alert flavour='warning' spaceAbove='medium'>
                    <strong>This guest card is not available today.</strong>
                    <p>
                        Bookings can only be made on these days: {fixedSessionActiveDayNames}.
                    </p>
                </Alert>
            )}
            {hasErrors && <ValidationErrorMessage />}
            <Input required
                label='Licence Plate'
                placeholder='AAA-123'
                autoComplete='off'
                format={(v) => formatPlate(v)}
                disabled={!!eventId || booking?.status === "FINALISED" || booking?.status === "CLOSED"}
                error={errors.guestPlate}
                {...{
                    ...text('guestPlate'),
                    onChange: (e) => {
                        setField('guestPlate', formatPlate(e.target.value));
                    }
                }}
            />

            <RateCardOptions
                rateCards={rateCards}
                error={errors.rateCard}
                value={values.rateCard}
                onChange={(rateCard) => {
                    setField('rateCard', rateCard);
                    if (rateCard?.isVip) setField('isVip', true);
                }}
            />

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <label htmlFor="vipBookingCheckbox" style={{ cursor: "pointer", fontWeight: 500 }}>
                    VIP booking
                </label>
                <CheckboxInput
                    id="vipBookingCheckbox"
                    checked={Boolean(values.isVip)}
                    disabled={booking?.status === "FINALISED" || booking?.status === "CLOSED"}
                    onChange={(event) => setField('isVip', event.target.checked)}
                />
            </div>

            {showVipContacts && (
                <div style={{ marginBottom: 16 }}>
                    { vipContacts.length === 0 && (
                      <Byline>
                        No contacts are configured yet.
                      </Byline>
                    )}

                    {vipContacts.map((contact) => {
                        const paged = pagedVipContactIds.includes(contact.id);
                        const reachable = Boolean(
                            contact.phone?.trim() || contact.email?.trim()
                        );
                        const locked = paged
                            || !reachable
                            || booking?.status === "FINALISED"
                            || booking?.status === "CLOSED";

                        return (
                            <div
                                key={contact.id}
                                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}
                            >
                                <label
                                    htmlFor={`vipContact_${contact.id}`}
                                    style={{ cursor: locked ? "default" : "pointer", fontWeight: 500 }}
                                >
                                    {contact.name}
                                    <span className={css({ marginLeft: 8, fontWeight: 400, color: paged || reachable ? undefined : red })}>
                                        {paged
                                            ? '· already paged'
                                            : reachable
                                                ? `· ${[contact.phone && 'SMS', contact.email && 'email'].filter(Boolean).join(' + ')}`
                                                : '· no phone or email'}
                                    </span>
                                </label>
                                <CheckboxInput
                                    id={`vipContact_${contact.id}`}
                                    checked={paged || vipContactIds.includes(contact.id)}
                                    disabled={locked}
                                    onChange={() => toggleVipContact(contact.id)}
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            {booking && booking.status &&
                <SelectInput
                    label='Status'
                    placeholder='Select a status'
                    options={enums.BOOKING_STATUS_OPTIONS_WITHOUT_FINALISED}
                    type='select'
                    disabled={booking.status === "FINALISED" || booking.status === "CLOSED"}
                    value={booking.status}
                    {...select("status")}
                />
            }
            <HR spaceAbove='medium' spaceBelow='medium' />

            {required.guestDetails ? <>
                <H2 spaceBelow='medium'>Customer details</H2>
                {required.guestName ? <Input required
                    label='Full name'
                    placeholder='John Smith'
                    autoComplete='off'
                    error={errors.guestName}
                    {...text('guestName')}
                /> : null}
                {required.guestEmail ? <Input required
                    label='Email address'
                    placeholder='name@email.com'
                    autoComplete='off'
                    error={errors.guestEmail}
                    {...email('guestEmail')}
                /> : null}
                {required.guestCompany ? <Input required
                    label='Company'
                    placeholder='Company'
                    autoComplete='off'
                    error={errors.guestCompany}
                    {...text('guestCompany')}
                /> : null}
                {required.guestDepartment ? <Input required
                    label='Department'
                    placeholder='Department'
                    autoComplete='off'
                    error={errors.guestDepartment}
                    {...text('guestDepartment')}
                /> : null}
                {required.guestPhone ? <PhoneNumberInput required
                    label='Contact number'
                    placeholder='400 123 456'
                    autoComplete='off'
                    defaultCountry={countryCode}
                    error={errors.guestPhone || badPhone}
                    value={values.guestPhone}
                    onValueChange={(v) => setField('guestPhone', v)}
                /> : null}
                {required.guestRoom ? <Input required
                    label='Room'
                    placeholder='Room 123'
                    autoComplete='off'
                    error={errors.guestRoom}
                    {...text('guestRoom')}
                /> : null}
                {required.guestApartmentNumber ? <Input required
                    label='Resident Apartment Number'
                    placeholder='Apt 1A'
                    autoComplete='off'
                    error={errors.guestApartmentNumber}
                    {...text('guestApartmentNumber')}
                /> : null}
                {required.guestReservation ? <Input required
                    label='Reservation'
                    placeholder='ABCD1234'
                    autoComplete='off'
                    error={errors.guestReservation}
                    {...text('guestReservation')}
                /> : null}
                {required.guestEmployeeCode ? <Input required
                    label='Employee Code'
                    placeholder='EMP1234'
                    autoComplete='off'
                    error={errors.guestEmployeeCode}
                    {...text('guestEmployeeCode')}
                /> : null}
                {required.guestPosition ? <Input required
                    label='Position'
                    placeholder='Manager'
                    autoComplete='off'
                    error={errors.guestPosition}
                    {...text('guestPosition')}
                /> : null}
                {required.includeCarType ? <Input required
                    label='Car Type'
                    placeholder='Car Type'
                    autoComplete='off'
                    error={errors.carType}
                    {...text('carType')}
                /> : null}
                {required.includeCarNotes ? <TextArea required
                    label='Car Notes'
                    placeholder='Car Notes'
                    autoComplete='off'
                    error={errors.carNotes}
                    {...text('carNotes')}
                /> : null}
            </> : null}

            {required.endDay ? <>
                <DateInput floatPicker
                    label={isFixedPackage ? 'Scheduled Entry (fixed)' : 'Scheduled Entry'}
                    placeholder='Enter entry date'
                    autoComplete='off'
                    displayFormat='ddd, D MMM YYYY'
                    isOutsideRange={isInactiveDayForCard}
                    isDayHighlighted={isToday}
                    error={errors.startDay || (isFixedSessionInactiveDate ? `This guest card is not available today. Bookings can only be made on these days: ${fixedSessionActiveDayNames}.` : undefined)}
                    type='text'
                    disabled={isFixedPackage}
                    {...raw({
                        name: 'startDay',
                        validate: validateStartDay
                    })}
                />

                {!isRateCardSingleDay && <DateInput floatPicker
                    label={isFixedPackage ? 'Scheduled Exit (fixed)' : 'Scheduled Exit'}
                    placeholder='Enter exit date'
                    autoComplete='off'
                    displayFormat='ddd, D MMM YYYY'
                    isOutsideRange={isInactiveDayForCard}
                    isDayHighlighted={(x) => isToday(x, installation.timeZoneName)}
                    error={errors.endDay}
                    type='text'
                    disabled={isFixedPackage}
                    {...raw({
                        name: 'endDay',
                        validate: validateEndDay
                    })}
                />}

                <HR spaceAbove='medium' spaceBelow='medium' />

                {parklioEnabled && rateCard && !booking ? <>
                    <AllocatedBaySection
                        bays={availableBays}
                        selectedBayId={selectedBayId}
                        onSelectBay={setSelectedBayId}
                        loading={baysLoading}
                        error={baysError}
                        onRetry={() => refetchBays()}
                    />
                    <HR spaceAbove='medium' spaceBelow='medium' />
                </> : null}

                <H2>Parking summary</H2>
                <ParkingSummaryHeader
                    installation={installation}
                    rateCard={rateCard}
                    startDay={startDateTime}
                    endDay={endDateTime}
                    errors={hasErrors}
                />
                {startDay && endDay ? <ParkingSummary
                    installation={installation}
                    rateCard={rateCard}
                    startDay={startDateTime}
                    endDay={endDateTime}
                    entryTime={eventId ? startedAt : null}
                    errors={hasErrors}
                    allocatedBay={selectedBay}
                /> : null}
                <HR spaceAbove='medium' spaceBelow='medium' />
            </> : null}

            {rateCard?.paymentStripe &&
                <>
                    <H2>Payment details</H2>
                    <StripeElementsInput
                        appConfig={appConfig}
                        currency={installation.currency}
                        country={facility?.city?.country}
                        installationId={installation.id}
                        guestEmail={values.guestEmail}
                        paymentMethod={paymentMethod}
                        setStripeCustomerId={(id) => {
                            setField('stripeCustomerId', id);
                            setField('stripeSource', null);
                            setCardConfirmedThisSession(true);
                        }}
                        {...raw('stripeSource')}
                    />
                    <HR spaceAbove='medium' spaceBelow='medium' />
                </>
            }
        </Container>

        <Container size='medium' spaceBelow='medium'>
            <ReactModal
                isOpen={isModalOpen}
                onRequestClose={() => setModalOpen(false)}
                className={modalStyles}
                overlayClassName={modalOverlayStyles}
            >
                {hasErrors && <ValidationErrorMessage />}
                <h1 className={css({ textAlign: 'left' })}>{rateCard?.name}</h1>
                {isRateCardFOC ? (
                    <>
                        <p style={{ fontSize: 'larger' }}>
                            Each Free of Charge booking requires a staff name and a booking reason.
                        </p>
                    </>
                ) : (
                    <>
                        <p style={{ fontSize: 'larger' }}>
                            Are you sure this customer has a parking booking on the hotel PMS (Opera or other)?
                            Drivo <span style={{ fontWeight: 'bold' }}>does not</span> charge via Opera automatically.
                        </p>
                        <p style={{ fontSize: 'larger' }}>
                            We strongly recommend using the Hotel Guest card if there is no prior
                            booking in the PMS.
                        </p>
                    </>
                )}

                <Input
                    required
                    error={errors.staffName}
                    placeholder='staff Name'
                    autoComplete='off'
                    label="Staff Name"
                    {...text('staffName')}
                />

                {(rateCard && isRateCardFOC) &&
                    <SelectInput
                        label='Booking reason'
                        placeholder='Select a reason'
                        options={enums.getBookingFocReasons(installation.id)}
                        type='select'
                        {...select("reason")}
                    />
                }

                <ColContainer>
                    <HollowButton disabled={!canSubmit} className={css({ marginTop: 30 })} onClick={_onSubmit}>
                        Save
                    </HollowButton>
                    <HollowButton className={css({ marginTop: 30 })} onClick={() => setModalOpen(false)}>
                        Cancel
                    </HollowButton>
                </ColContainer>
            </ReactModal>

            <ReactModal
                isOpen={isStaffModalOpen}
                onRequestClose={() => (setStaffModalOpen(false))}
                className={modalStyles}
                overlayClassName={modalOverlayStyles}
            >
                <p style={{ fontSize: 'larger', textAlign: 'center' }}>
                    Are you sure you want to edit this booking? This action will be notified to management.
                </p>
                <ColContainer>
                    <HollowButton className={css({ marginTop: 30 })} onClick={_onSubmit}>
                        Proceed
                    </HollowButton>
                    <HollowButton className={css({ marginTop: 30 })} onClick={() => setStaffModalOpen(false)}>
                        Cancel
                    </HollowButton>
                </ColContainer>
            </ReactModal>

            <Button
                block flavour='primary'
                disabled={!canSubmit}
                onClick={() => {
                    const rateCardName = rateCard?.name?.toLowerCase();
                    const publicUrl = rateCard?.publicUrl?.toLowerCase();

                    if (rateCardName.match('chargeback|free of charge|free|foc|opera') != null) setModalOpen(true);
                    else if (rateCard && (rateCardName.includes('staff') || (publicUrl && publicUrl.includes('staff')))) setStaffModalOpen(true);
                    else _onSubmit();
                }}
            >
                {booking ? 'Update booking' : 'Create booking'}
            </Button>
            <NakedButton block flavour='gray' onClick={onCancel}>
                Cancel
            </NakedButton>
        </Container>
    </form>
})

export function BookingCreate({
    installation,
    facility,
    onCancel,
    onSubmit,
    plate,
    rateCards,
    eventId
}) {
    const { data = {}, loading, error } = useQuery(eventById, {
        skip: !eventId,
        variables: { id: eventId }
    });

    const { Event: event } = data
    const defaultPlate = plate || (event && event.plate) || ''
    const startedAt = event ? DateTime.fromISO(event.pcLoggedAt, { locale: "en-AU" }).setZone(installation.timeZoneName) : DateTime.now().setZone(installation.timeZoneName)

    return (
        <div>
            <BookingHeader title='New Booking' startedAt={startedAt?.toJSDate?.()} installationId={installation.id} />
            {error ? <LoadingError error={error} /> : null}
            {loading && <Loading />}
            {!loading && <BookingForm
                eventId={event ? event.id : null}
                defaultPlate={defaultPlate}
                installation={installation}
                facility={facility}
                rateCards={rateCards}
                startedAt={startedAt}
                onCancel={onCancel}
                onSubmit={onSubmit}
            />}
        </div>
    )
}

export function BookingEdit({
    installation,
    facility,
    bookingId,
    onCancel,
    onSubmit,
    rateCards
}) {
    const [booking, setBooking] = useState(null)
    const [bookingDetails, setBookingDetails] = useState(null)
    const {
        data = {},
        loading,
        error
    } = useQuery(bookingById, {
        fetchPolicy: 'no-cache',
        variables: { bookingId, includeParkingSessions: false, includeVipAlertLogs: false },
    })

    useEffect(() => {
        if (!data) return;

        setBooking(data.booking)
    }, [data])

    useEffect(() => {
        if (!booking) return;

        const fetchData = async () => {
            try {
                const res = await details(booking);
                setBookingDetails(res);
            } catch (e) {
                console.error(e.message)
            }
        };

        fetchData();
    }, [booking])

    if (error) return <LoadingError error={error} />
    if (loading) return <Loading />

    if (!booking) {
        return <Container size='large' spaceBelow='medium'>
            <H1>Not found</H1>
        </Container>
    }

    return <div>
        <BookingHeader title={booking?.plate} startedAt={bookingDetails?.startedAt?.toJSDate?.()} installationId={installation.id} />
        <BookingForm
            eventId={booking.initialVisit ? booking.initialVisit.id : null}
            installation={installation}
            facility={facility}
            booking={booking}
            defaultPlate={booking?.plate}
            rateCards={rateCards}
            startedAt={bookingDetails?.startedAt}
            onCancel={onCancel}
            onSubmit={onSubmit}
        />
    </div>
}

export default withInstallation(function BookingCreateEdit({
    match,
    location,
    history,
    installation,
    facility,
}) {
    const rateQ = useQuery(allActiveRateCards, {
        fetchPolicy: 'no-cache',
        variables: {
            installationId: installation.id
        },
    })

    if (rateQ.loading) return (
        <CardContainer>
            <Loading />
        </CardContainer>
    )
    if (rateQ.error) return (
        <CardContainer>
            <LoadingError error={rateQ.error} />
        </CardContainer>
    )
    const rateCards = rateQ.data.allRateCards
    if (!rateCards.length) return (

        <CardContainer>
            <LoadingError error='Missing guest cards' />
        </CardContainer>

    )
    const {
        bookingId,
        eventId,
        plate
    } = qs.parse(location.search, { ignoreQueryPrefix: true });

    function onSubmit({ id, guestName }) {
        history.push(match.url.replace('/create', `/${id}`), {
            flash: {
                flavour: 'success',
                message: `Booking for ${guestName} created successfully`,
                createdAt: Date.now(),
            },
        });
    }

    function onCancel() {
        history.goBack()
    }

    if (bookingId) {
        return (
            <CardContainer>
                <BookingEdit
                    installation={installation}
                    facility={facility}
                    bookingId={bookingId}
                    rateCards={rateCards}
                    onCancel={onCancel}
                    onSubmit={onSubmit}
                />
            </CardContainer>

        )
    }
    return (
        <CardContainer>
            <BookingCreate
                installation={installation}
                facility={facility}
                plate={plate}
                rateCards={rateCards}
                eventId={eventId}
                onCancel={onCancel}
                onSubmit={onSubmit}
            />
        </CardContainer>


    )

})
